import { useEffect, useState } from "react";
import SunMoon from "./_gears/SunMoon";
import { HStack, XDiv, XText } from "IZOX/_X";
import { ColorX, LocaleX } from "IZOX/STATIC";

/**
 * @typedef {Object} langcolorsparam
 * @property {String} offBgColor
 * @property {String} onBgColor
 * @property {String} offSwColor
 * @property {String} onSwColor
 * @property {String} offBtnColor
 * @property {String} onBtnColor
 * 
 * @typedef {Object} langimageparam
 * @property {*} uncheck
 * @property {*} check
 * 
 * @typedef {Object} langswitchparams
 * @property {Number} size
 * @property {(tf: Boolean) => void} onChange
 * @property {Boolean} defaultValue
 * @property {Boolean} noBoxShadow
 * @property {"linear" | "ease" | "ease-in" | "ease-out" | "ease-in-out"} timingFunction
 * @property {Number} animationDuration
 * @property {langimageparam} image
 * @property {langcolorsparam} colors
 * @property {import("react").CSSProperties} style
 * @property {Boolean} disabled
 * @property {import("IZOX/__Typedef/common").lang} label
 * @property {Number} fontSize
 * @property {String} fontColor
 * 
 * @param {langswitchparams} param0 
 * @returns 
 */
function QSwitch({
  size = 18,
  onChange = undefined,
  defaultValue = false,
  noBoxShadow = false,
  timingFunction = "ease-out",
  animationDuration = 500,
  image = {
    uncheck: undefined,
    check: undefined
  },
  colors = {
    offBgColor: ColorX.CSS("aliceblue"),
    onBgColor: ColorX.CSS("cmBlue"),
    offSwColor: ColorX.CSS("pureWhite"),
    onSwColor: ColorX.CSS("pureWhite"),
    offBtnColor: ColorX.CSS("cmBlue"),
    onBtnColor: ColorX.CSS("cmBlue"),
    offFontColor: ColorX.CSS("cmBlue"),
    onFontColor: ColorX.CSS("aliceblue")
  },
  style = {},
  disabled = false,
  label = "",
  fontSize = 12,
  ...others
}){

  const [value, setValue] = useState(defaultValue);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const onMouseEnter = () => {
    setHover(true);
  };

  const onMouseLeave = () => {
    setHover(false);
  };
  
  const factor = size / 146;

  const _onToggle = () => {
    if(disabled) return;
    onChange?.(!value);
    setValue(!value);
  };

  return (
    <HStack fitWidthToContent flexShrink={0} round={50} 
      colorTransition bgColor={value? colors.onBgColor : colors.offBgColor}
      bW={2} bColor={colors.onBgColor} pV={2} pR={10} pL={2} {...others}>
      <XDiv onClick={_onToggle}
        onMouseEnter={onMouseEnter} 
        onMouseLeave={onMouseLeave}
        style={{
          cursor: disabled? "not-allowed" : "pointer",
          overflow: "hidden",
          width: 300 * factor,
          height: 146 * factor,
          borderRadius: 146 * factor,
          boxShadow: noBoxShadow ? undefined : `0 ${10*factor}px ${8*factor}px 0 rgba(51, 83, 113, .26) inset,
                0 -${10*factor}px ${8*factor}px 0 rgba(85, 123, 158, .26) inset,
                0 ${3*factor}px ${2*factor}px ${1*factor}px rgb(239, 245, 249),
                0 ${6*factor}px ${2*factor}px ${1*factor}px rgb(216, 223, 227)`,
          ...style
        }}>
        <XDiv style={{
          position: "relative",
          width: "100%",
          height: "100%",
          background: value ? 
            (colors?.onSwColor ?? "rgb(30, 34, 60)") 
            : (colors?.offSwColor ?? "rgb(60, 134, 188)"),
          transition: `all ${animationDuration}ms ${timingFunction}`
        }}>
          <SunMoon image={image} factor={factor} 
            hover={hover} checked={value} colors={colors}
            animationDuration={animationDuration} 
            timingFunction={timingFunction}/>
        </XDiv>
      </XDiv>
      <HStack mL={10} fitWidthToContent>
        <XText fontSize={fontSize} noWrap 
          color={value? colors.onFontColor : colors.offFontColor} bold>
          {LocaleX.Parse(label)}
        </XText>
      </HStack>
    </HStack>
  );
}

export default QSwitch;